/**
 * GroupCashFlowDetail v2 — full cash flow statement.
 *
 * Demo polish:
 *   - Optional `businessId` to scope to one business
 *   - Defensive `?? 0` on every numeric
 *   - Scope badge in header
 *   - Per-business breakdown hidden when scoped
 */
import React from "react";
import GroupCompletenessNotice, { type GroupReportMeta } from "@/components/group/GroupCompletenessNotice";
import {
  Loader2,
  AlertTriangle,
  Banknote,
  ArrowDownToLine,
  ArrowUpFromLine,
  Building2,
  Wallet,
} from "lucide-react";
import { useGroupCashFlow } from "@/hooks/useGroupReports";
import ReportExportMenu from "./ReportExportMenu";
import { useDisplayCurrency } from "@/context/DisplayCurrencyContext";

interface Props {
  from: string;
  to: string;
  businessId?: string | null;
  businessLabel?: string | null;
}

function num(v: unknown): number {
  const n = typeof v === "number" ? v : Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
}

const GroupCashFlowDetail: React.FC<Props> = ({
  from,
  to,
  businessId,
  businessLabel,
}) => {
  const { data, isLoading, isError } = useGroupCashFlow({
    from,
    to,
    businessId,
  });
  const { format } = useDisplayCurrency();

  const dateRangeLabel = `${formatDate(from)} – ${formatDate(to)}`;
  const scopeLabel =
    businessId && businessLabel ? businessLabel : "All businesses";

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="rounded-md border border-destructive/20 bg-destructive/5 p-4 text-sm text-destructive flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 mt-0.5" />
        <div>
          <div className="font-medium">Couldn't load cash flow</div>
          <div className="opacity-80 mt-1">
            Try a different date range, or refresh.
          </div>
        </div>
      </div>
    );
  }

  const operating = num(data.operating?.total);
  const investing = num(data.investing?.total);
  const financing = num(data.financing?.total);
  const netChange = num(data.netChange ?? operating + investing + financing);
  const opening = num(data.openingBalance);
  const closing = num(data.closingBalance ?? opening + netChange);

  const operatingLines = (data.operating?.lines || []).map((l) => ({
    label: l.label || "—",
    amountMinor: num(l.amountMinor),
  }));
  const investingLines = (data.investing?.lines || []).map((l) => ({
    label: l.label || "—",
    amountMinor: num(l.amountMinor),
  }));
  const financingLines = (data.financing?.lines || []).map((l) => ({
    label: l.label || "—",
    amountMinor: num(l.amountMinor),
  }));

  const perBusiness =
    !businessId && data.perBusiness?.length ? data.perBusiness : null;
  const hasAnyActivity =
    operatingLines.length || investingLines.length || financingLines.length;

  // ─ Export ─

  const getCsvData = () => {
    const rows: Array<Array<string | number>> = [];
    rows.push(["OPERATING ACTIVITIES", ""]);
    operatingLines.forEach((l) =>
      rows.push([`  ${l.label}`, format(l.amountMinor)]),
    );
    rows.push(["Net cash from operating", format(operating)]);
    rows.push(["", ""]);
    if (investingLines.length) {
      rows.push(["INVESTING ACTIVITIES", ""]);
      investingLines.forEach((l) =>
        rows.push([`  ${l.label}`, format(l.amountMinor)]),
      );
      rows.push(["Net cash from investing", format(investing)]);
      rows.push(["", ""]);
    }
    if (financingLines.length) {
      rows.push(["FINANCING ACTIVITIES", ""]);
      financingLines.forEach((l) =>
        rows.push([`  ${l.label}`, format(l.amountMinor)]),
      );
      rows.push(["Net cash from financing", format(financing)]);
      rows.push(["", ""]);
    }
    rows.push(["NET CHANGE IN CASH", format(netChange)]);
    rows.push(["Opening balance", format(opening)]);
    rows.push(["Closing balance", format(closing)]);
    return { headers: ["Activity", "Amount"], rows };
  };

  const getPdfSections = () => {
    const sections: Array<{
      heading?: string;
      headers?: string[];
      rows?: Array<Array<string | number>>;
      totalRow?: { label: string; value: string };
    }> = [];

    sections.push({
      heading: "Summary",
      headers: ["Activity", "Amount"],
      rows: [
        ["Operating activities", format(operating)],
        ["Investing activities", format(investing)],
        ["Financing activities", format(financing)],
        ["Net change in cash", format(netChange)],
        ["Opening balance", format(opening)],
        ["Closing balance", format(closing)],
      ],
    });

    if (operatingLines.length) {
      sections.push({
        heading: "Operating activities",
        headers: ["Description", "Amount"],
        rows: operatingLines.map((l) => [l.label, format(l.amountMinor)]),
        totalRow: {
          label: "Net cash from operating",
          value: format(operating),
        },
      });
    }

    if (investingLines.length) {
      sections.push({
        heading: "Investing activities",
        headers: ["Description", "Amount"],
        rows: investingLines.map((l) => [l.label, format(l.amountMinor)]),
        totalRow: {
          label: "Net cash from investing",
          value: format(investing),
        },
      });
    }

    if (financingLines.length) {
      sections.push({
        heading: "Financing activities",
        headers: ["Description", "Amount"],
        rows: financingLines.map((l) => [l.label, format(l.amountMinor)]),
        totalRow: {
          label: "Net cash from financing",
          value: format(financing),
        },
      });
    }

    if (perBusiness) {
      sections.push({
        heading: "Per-business cash flow",
        headers: ["Business", "Operating", "Net change"],
        rows: perBusiness.map((b) => [
          b.businessName,
          format(num(b.operatingMinor)),
          format(num(b.netChangeMinor)),
        ]),
      });
    }

    return sections;
  };

  const getShareSummary = () =>
    [
      `Cash Flow — ${scopeLabel}`,
      `${dateRangeLabel}`,
      ``,
      `Operating:   ${format(operating)}`,
      `Investing:   ${format(investing)}`,
      `Financing:   ${format(financing)}`,
      `Net change:  ${format(netChange)}`,
      `Closing bal: ${format(closing)}`,
    ].join("\n");

  // ─ Render ─

  return (
    <section className="space-y-5">
      <GroupCompletenessNotice meta={data as GroupReportMeta | undefined} />
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-0.5">
            <h2 className="text-base font-semibold text-foreground">
              Cash Flow
            </h2>
            <ScopeBadge label={scopeLabel} businessId={businessId} />
          </div>
          <p className="text-xs text-muted-foreground">{dateRangeLabel}</p>
        </div>
        <ReportExportMenu
          title={`Cash Flow — ${scopeLabel}`}
          subtitle={dateRangeLabel}
          getCsvData={getCsvData}
          getPdfSections={getPdfSections}
          getShareSummary={getShareSummary}
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <HeadlineCard
          icon={Banknote}
          label="Operating"
          value={format(operating)}
          tone={operating >= 0 ? "green" : "rose"}
        />
        <HeadlineCard
          icon={ArrowDownToLine}
          label="Investing"
          value={format(investing)}
          tone={investing >= 0 ? "green" : "slate"}
        />
        <HeadlineCard
          icon={ArrowUpFromLine}
          label="Financing"
          value={format(financing)}
          tone={financing >= 0 ? "green" : "slate"}
        />
        <HeadlineCard
          icon={Wallet}
          label="Net change"
          value={format(netChange)}
          tone={netChange >= 0 ? "green" : "rose"}
        />
      </div>

      <div className="rounded-lg border bg-muted/30 p-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
            Opening balance
          </div>
          <div className="text-lg font-semibold tabular-nums">
            {format(opening)}
          </div>
        </div>
        <div className="text-muted-foreground text-sm flex items-center gap-2">
          <span>+</span>
          <span className="tabular-nums">{format(netChange)}</span>
          <span>=</span>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
            Closing balance
          </div>
          <div className="text-lg font-bold tabular-nums text-[#4CAF50]">
            {format(closing)}
          </div>
        </div>
      </div>

      {operatingLines.length > 0 && (
        <ActivityTable
          title="Operating activities"
          lines={operatingLines}
          totalLabel="Net cash from operating"
          totalMinor={operating}
          format={format}
        />
      )}
      {investingLines.length > 0 && (
        <ActivityTable
          title="Investing activities"
          lines={investingLines}
          totalLabel="Net cash from investing"
          totalMinor={investing}
          format={format}
        />
      )}
      {financingLines.length > 0 && (
        <ActivityTable
          title="Financing activities"
          lines={financingLines}
          totalLabel="Net cash from financing"
          totalMinor={financing}
          format={format}
        />
      )}

      {!hasAnyActivity && (
        <div className="rounded-md border bg-muted/30 p-8 text-center text-sm text-muted-foreground">
          No cash flow activity in this period.
        </div>
      )}

      {perBusiness && (
        <div className="rounded-lg border bg-card overflow-hidden">
          <div className="px-4 py-3 border-b bg-muted/40">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <Building2 className="h-3.5 w-3.5" />
              Per-business cash flow
            </h3>
          </div>
          <table className="w-full text-sm">
            <thead className="border-b">
              <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                <th className="font-medium py-2 px-4">Business</th>
                <th className="font-medium py-2 px-4 text-right">Operating</th>
                <th className="font-medium py-2 px-4 text-right">Net change</th>
              </tr>
            </thead>
            <tbody>
              {perBusiness.map((b) => (
                <tr key={b.businessId} className="border-b last:border-0">
                  <td className="py-2.5 px-4 font-medium">{b.businessName}</td>
                  <td className="py-2.5 px-4 text-right tabular-nums">
                    {format(num(b.operatingMinor))}
                  </td>
                  <td
                    className={`py-2.5 px-4 text-right tabular-nums font-medium ${num(b.netChangeMinor) < 0 ? "text-rose-600" : "text-[#4CAF50]"}`}
                  >
                    {format(num(b.netChangeMinor))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

// ─── Helpers ──────────────────────────────────────────────────────

const ScopeBadge: React.FC<{
  label: string;
  businessId?: string | null;
}> = ({ label, businessId }) => (
  <span
    className={[
      "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider",
      businessId
        ? "bg-[#4CAF50]/10 text-[#4CAF50]"
        : "bg-muted text-muted-foreground",
    ].join(" ")}
  >
    <Building2 className="h-2.5 w-2.5" />
    {label}
  </span>
);

const HeadlineCard: React.FC<{
  icon: React.ElementType;
  label: string;
  value: string;
  tone?: "green" | "rose" | "slate";
}> = ({ icon: Icon, label, value, tone = "slate" }) => {
  const valueColor =
    tone === "green"
      ? "text-[#4CAF50]"
      : tone === "rose"
        ? "text-rose-600"
        : "text-foreground";
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
          {label}
        </div>
        <Icon className={`h-3.5 w-3.5 ${valueColor}`} />
      </div>
      <div className={`text-xl font-bold tabular-nums ${valueColor}`}>
        {value}
      </div>
    </div>
  );
};

const ActivityTable: React.FC<{
  title: string;
  lines: Array<{ label: string; amountMinor: number }>;
  totalLabel: string;
  totalMinor: number;
  format: (m: number) => string;
}> = ({ title, lines, totalLabel, totalMinor, format }) => (
  <div className="rounded-lg border bg-card overflow-hidden">
    <div className="px-4 py-3 border-b bg-muted/40">
      <h3 className="text-sm font-semibold">{title}</h3>
    </div>
    <table className="w-full text-sm">
      <tbody>
        {lines.map((l, i) => (
          <tr key={i} className="border-b last:border-0 hover:bg-muted/20">
            <td className="py-2 px-4">{l.label}</td>
            <td
              className={`py-2 px-4 text-right tabular-nums ${l.amountMinor < 0 ? "text-rose-600" : ""}`}
            >
              {format(l.amountMinor)}
            </td>
          </tr>
        ))}
        <tr className="bg-muted/30 font-semibold">
          <td className="py-2.5 px-4">{totalLabel}</td>
          <td
            className={`py-2.5 px-4 text-right tabular-nums ${totalMinor < 0 ? "text-rose-600" : ""}`}
          >
            {format(totalMinor)}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
);

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default GroupCashFlowDetail;
